import React, { useState, useRef } from 'react';
import { 
  Upload, 
  Type, 
  Palette, 
  Download, 
  Eye, 
  Zap,
  Image as ImageIcon,
  Settings,
  Grid,
  Copy,
  Check,
  AlertCircle,
  Loader2,
  Play
} from 'lucide-react';

export default function AdsBuilder() {
  const [inputs, setInputs] = useState({
    logo: null,
    text: 'Your Amazing Product',
    image: null,
    backgroundColor: '#ffffff',
    textColor: '#000000',
    ctaText: 'Shop Now'
  });
  
  const [generatedBanners, setGeneratedBanners] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  
  const logoInputRef = useRef();
  const imageInputRef = useRef();

  // Standard ad placement sizes
  const placementSizes = [
    // Google Ads Display
    { name: 'Leaderboard', width: 728, height: 90, platform: 'Google Ads', category: 'desktop' },
    { name: 'Banner', width: 468, height: 60, platform: 'Google Ads', category: 'desktop' },
    { name: 'Medium Rectangle', width: 300, height: 250, platform: 'Google Ads', category: 'universal' },
    { name: 'Large Rectangle', width: 336, height: 280, platform: 'Google Ads', category: 'desktop' },
    { name: 'Skyscraper', width: 160, height: 600, platform: 'Google Ads', category: 'desktop' },
    { name: 'Wide Skyscraper', width: 300, height: 600, platform: 'Google Ads', category: 'desktop' },
    
    // Facebook/Meta Ads
    { name: 'Facebook Feed', width: 1200, height: 628, platform: 'Facebook', category: 'social' },
    { name: 'Instagram Post', width: 1080, height: 1080, platform: 'Instagram', category: 'social' },
    { name: 'Instagram Story', width: 1080, height: 1920, platform: 'Instagram', category: 'mobile' },
    { name: 'Facebook Cover', width: 1200, height: 315, platform: 'Facebook', category: 'social' },
    
    // LinkedIn
    { name: 'LinkedIn Feed', width: 1200, height: 627, platform: 'LinkedIn', category: 'social' },
    { name: 'LinkedIn Banner', width: 1584, height: 396, platform: 'LinkedIn', category: 'social' },
    
    // Mobile
    { name: 'Mobile Banner', width: 320, height: 50, platform: 'Mobile', category: 'mobile' },
    { name: 'Mobile Large', width: 320, height: 100, platform: 'Mobile', category: 'mobile' },
    
    // YouTube
    { name: 'YouTube Display', width: 300, height: 250, platform: 'YouTube', category: 'video' },
    { name: 'YouTube Overlay', width: 480, height: 70, platform: 'YouTube', category: 'video' }
  ];

  const handleFileUpload = (type, file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setInputs(prev => ({
          ...prev,
          [type]: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const generateBanners = async () => {
    setIsGenerating(true);
    
    // Initialize banners without previews
    const banners = placementSizes.map((size, index) => ({
      id: index,
      ...size,
      preview: null,
      isReady: false
    }));
    
    setGeneratedBanners(banners);
    
    // Generate banners progressively
    for (let i = 0; i < banners.length; i++) {
      try {
        // Wait for images to load before generating
        await new Promise(resolve => setTimeout(resolve, 300));
        
        const preview = await generateBannerPreview(placementSizes[i]);
        
        setGeneratedBanners(prev => 
          prev.map((banner, idx) => 
            idx === i ? { 
              ...banner, 
              preview: preview,
              isReady: true 
            } : banner
          )
        );
      } catch (error) {
        console.error(`Error generating banner ${i}:`, error);
        // Set as ready even if failed, with fallback
        setGeneratedBanners(prev => 
          prev.map((banner, idx) => 
            idx === i ? { 
              ...banner, 
              preview: createFallbackPreview(placementSizes[i]),
              isReady: true 
            } : banner
          )
        );
      }
    }
    
    setIsGenerating(false);
  };

  const createFallbackPreview = (size) => {
    // Simple SVG fallback if canvas fails
    return `data:image/svg+xml,${encodeURIComponent(`
      <svg width="${size.width}" height="${size.height}" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="${inputs.backgroundColor}"/>
        <text x="50%" y="50%" text-anchor="middle" dy=".3em" 
              font-family="Arial, sans-serif" 
              font-size="${Math.min(size.width, size.height) * 0.1}" 
              fill="${inputs.textColor}">
          ${inputs.text}
        </text>
        <rect x="${size.width * 0.2}" y="${size.height * 0.7}" 
              width="${size.width * 0.6}" height="${size.height * 0.15}" 
              fill="${inputs.textColor}" rx="5"/>
        <text x="50%" y="${size.height * 0.78}" text-anchor="middle" dy=".3em" 
              font-family="Arial, sans-serif" 
              font-size="${Math.min(size.width, size.height) * 0.06}" 
              fill="${inputs.backgroundColor}">
          ${inputs.ctaText}
        </text>
      </svg>
    `)}`;
  };

  const generateBannerPreview = async (size) => {
    // Create canvas element
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = size.width;
    canvas.height = size.height;
    
         // Start with transparent background - we'll add background selectively
     ctx.clearRect(0, 0, size.width, size.height);
    
    try {
      // Calculate layout based on banner size
      const isWide = size.width > size.height * 1.5;
      const isTall = size.height > size.width * 1.5;
      const isSquare = Math.abs(size.width - size.height) < 100;
      
      let logoSize, textSize, ctaSize, productSize;
      let logoX, logoY, textY, ctaY, productX, productY;
      
      if (isWide) {
        // Wide banners (leaderboard, etc.)
        logoSize = Math.min(size.height * 0.6, 80);
        textSize = Math.min(size.height * 0.25, 24);
        ctaSize = Math.min(size.height * 0.15, 14);
        productSize = Math.min(size.height * 0.8, 100);
        
        logoX = 20;
        logoY = (size.height - logoSize) / 2;
        textY = size.height * 0.4;
        ctaY = size.height * 0.7;
        productX = size.width - productSize - 20;
        productY = (size.height - productSize) / 2;
      } else if (isTall) {
        // Tall banners (skyscraper, story)
        logoSize = Math.min(size.width * 0.6, 80);
        textSize = Math.min(size.width * 0.25, 24);
        ctaSize = Math.min(size.width * 0.15, 14);
        productSize = Math.min(size.width * 0.8, 120);
        
        logoX = (size.width - logoSize) / 2;
        logoY = 20;
        textY = size.height * 0.4;
        ctaY = size.height * 0.9;
        productX = (size.width - productSize) / 2;
        productY = size.height * 0.5;
      } else {
        // Square or rectangle banners
        logoSize = Math.min(size.width * 0.3, size.height * 0.3, 80);
        textSize = Math.min(size.width * 0.15, size.height * 0.15, 24);
        ctaSize = Math.min(size.width * 0.1, size.height * 0.1, 16);
        productSize = Math.min(size.width * 0.4, size.height * 0.4, 120);
        
        logoX = 20;
        logoY = 20;
        textY = size.height * 0.6;
        ctaY = size.height * 0.85;
        productX = size.width - productSize - 20;
        productY = size.height - productSize - 20;
      }
      
             // Load and draw images
       const imagePromises = [];
       
       // Load product image if available
       if (inputs.image) {
         const productImagePromise = new Promise((resolve) => {
           const productImg = new Image();
           productImg.onload = () => {
             // Calculate aspect ratio and fit image
             const aspectRatio = productImg.width / productImg.height;
             let drawWidth = productSize;
             let drawHeight = productSize;
             
             if (aspectRatio > 1) {
               drawHeight = productSize / aspectRatio;
             } else {
               drawWidth = productSize * aspectRatio;
             }
             
             const centerX = productX + (productSize - drawWidth) / 2;
             const centerY = productY + (productSize - drawHeight) / 2;
             
             // Use source-over to draw PNG with transparency preserved
             ctx.globalCompositeOperation = 'source-over';
             ctx.drawImage(productImg, centerX, centerY, drawWidth, drawHeight);
             resolve();
           };
           productImg.onerror = () => resolve(); // Continue even if image fails
           productImg.crossOrigin = 'anonymous'; // Enable CORS for images
           productImg.src = inputs.image;
         });
         imagePromises.push(productImagePromise);
       }
       
       // Load logo if available
       if (inputs.logo) {
         const logoImagePromise = new Promise((resolve) => {
           const logoImg = new Image();
           logoImg.onload = () => {
             const aspectRatio = logoImg.width / logoImg.height;
             let drawWidth = logoSize;
             let drawHeight = logoSize;
             
             if (aspectRatio > 1) {
               drawHeight = logoSize / aspectRatio;
             } else {
               drawWidth = logoSize * aspectRatio;
             }
             
             const centerX = logoX + (logoSize - drawWidth) / 2;
             const centerY = logoY + (logoSize - drawHeight) / 2;
             
             // Preserve PNG transparency
             ctx.globalCompositeOperation = 'source-over';
             ctx.drawImage(logoImg, centerX, centerY, drawWidth, drawHeight);
             resolve();
           };
           logoImg.onerror = () => resolve(); // Continue even if image fails
           logoImg.crossOrigin = 'anonymous'; // Enable CORS for images
           logoImg.src = inputs.logo;
         });
         imagePromises.push(logoImagePromise);
       }
       
       // Wait for all images to load before drawing text
       await Promise.all(imagePromises);
      
             // DON'T draw background yet - we'll add it after PNGs to preserve transparency
       
       // Draw text
       ctx.fillStyle = inputs.textColor;
       ctx.font = `bold ${textSize}px Arial, sans-serif`;
       ctx.textAlign = 'center';
       
       // Word wrap for main text
       const words = inputs.text.split(' ');
       const maxWidth = size.width * 0.8;
       let line = '';
       let y = textY;
       const lineHeight = textSize * 1.2;
       
       for (let i = 0; i < words.length; i++) {
         const testLine = line + words[i] + ' ';
         const metrics = ctx.measureText(testLine);
         const testWidth = metrics.width;
         
         if (testWidth > maxWidth && i > 0) {
           ctx.fillText(line, size.width / 2, y);
           line = words[i] + ' ';
           y += lineHeight;
         } else {
           line = testLine;
         }
       }
       ctx.fillText(line, size.width / 2, y);
      
      // Draw CTA button
      const ctaWidth = Math.min(size.width * 0.6, 200);
      const ctaHeight = ctaSize * 2;
      const ctaX = (size.width - ctaWidth) / 2;
      
      // CTA background
      ctx.fillStyle = inputs.textColor;
      ctx.fillRect(ctaX, ctaY - ctaHeight/2, ctaWidth, ctaHeight);
      
      // CTA text
      ctx.fillStyle = inputs.backgroundColor;
      ctx.font = `bold ${ctaSize}px Arial, sans-serif`;
      ctx.fillText(inputs.ctaText, size.width / 2, ctaY + ctaSize/3);
      
             // Size label  
       ctx.fillStyle = '#666';
       ctx.font = '12px Arial, sans-serif';
       ctx.textAlign = 'left';
       ctx.fillText(`${size.width}x${size.height}`, 10, 20);
       
       // NOW add background BEHIND everything to preserve PNG transparency
       ctx.globalCompositeOperation = 'destination-over';
       ctx.fillStyle = inputs.backgroundColor;
       ctx.fillRect(0, 0, size.width, size.height);
       
       // Reset to normal composite mode
       ctx.globalCompositeOperation = 'source-over';
      
    } catch (error) {
      console.error('Error generating banner:', error);
    }
    
    return canvas.toDataURL('image/png');
  };

  const downloadBanner = (banner) => {
    // Create download link
    const link = document.createElement('a');
    link.href = banner.preview;
    
    // Determine file extension based on data URL
    const isCanvas = banner.preview.startsWith('data:image/png');
    const extension = isCanvas ? 'png' : 'svg';
    
    link.download = `${banner.name.replace(/\s+/g, '_')}_${banner.width}x${banner.height}.${extension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyBannerCode = (banner) => {
    const code = `<img src="${banner.preview}" alt="${banner.name}" width="${banner.width}" height="${banner.height}">`;
    navigator.clipboard.writeText(code);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-semibold text-gray-800">Ads Builder</h1>
            </div>
            <div className="text-sm text-gray-500">
              Multi-Platform Banner Generator
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setIsPreviewMode(!isPreviewMode)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                isPreviewMode 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Eye className="w-4 h-4" />
              Preview Mode
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-1">
        {/* Left Panel - Input Controls */}
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Banner Content</h2>
            
            <div className="space-y-6">
              {/* Logo Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo
                </label>
                <div 
                  onClick={() => logoInputRef.current?.click()}
                  className="w-full h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-400 transition-colors"
                >
                  {inputs.logo ? (
                    <img src={inputs.logo} alt="Logo" className="max-h-20 max-w-20 object-contain" />
                  ) : (
                    <div className="text-center">
                      <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                      <span className="text-sm text-gray-500">Upload Logo</span>
                    </div>
                  )}
                </div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileUpload('logo', e.target.files[0])}
                />
              </div>

              {/* Main Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Main Text
                </label>
                <input
                  type="text"
                  value={inputs.text}
                  onChange={(e) => setInputs(prev => ({ ...prev, text: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your main message"
                />
              </div>

              {/* CTA Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Call-to-Action
                </label>
                <input
                  type="text"
                  value={inputs.ctaText}
                  onChange={(e) => setInputs(prev => ({ ...prev, ctaText: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Shop Now, Learn More"
                />
              </div>

              {/* Product Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product Image (PNG)
                </label>
                <div 
                  onClick={() => imageInputRef.current?.click()}
                  className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-400 transition-colors"
                >
                  {inputs.image ? (
                    <img src={inputs.image} alt="Product" className="max-h-28 max-w-28 object-contain" />
                  ) : (
                    <div className="text-center">
                      <ImageIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <span className="text-sm text-gray-500">Upload Product Image</span>
                    </div>
                  )}
                </div>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  onChange={(e) => handleFileUpload('image', e.target.files[0])}
                />
              </div>

              {/* Color Controls */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Background
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={inputs.backgroundColor}
                      onChange={(e) => setInputs(prev => ({ ...prev, backgroundColor: e.target.value }))}
                      className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={inputs.backgroundColor}
                      onChange={(e) => setInputs(prev => ({ ...prev, backgroundColor: e.target.value }))}
                      className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Text Color
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={inputs.textColor}
                      onChange={(e) => setInputs(prev => ({ ...prev, textColor: e.target.value }))}
                      className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={inputs.textColor}
                      onChange={(e) => setInputs(prev => ({ ...prev, textColor: e.target.value }))}
                      className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Generate Button */}
          <div className="p-6">
            <button
              onClick={generateBanners}
              disabled={isGenerating || !inputs.text}
              className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-all ${
                isGenerating || !inputs.text
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:from-blue-600 hover:to-purple-700'
              }`}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating Banners...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Generate All Banners
                </>
              )}
            </button>
            
            {!inputs.text && (
              <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Please enter main text to continue
              </p>
            )}
          </div>
        </div>

        {/* Main Content - Generated Banners */}
        <div className="flex-1 p-6">
          {generatedBanners.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center text-gray-500">
                <Grid className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-800 mb-2">No Banners Generated Yet</h3>
                <p className="text-sm">Fill in your content details and click "Generate All Banners" to start</p>
              </div>
            </div>
          ) : (
            <div>
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-2">Generated Banners</h2>
                <p className="text-sm text-gray-600">
                  {generatedBanners.filter(b => b.isReady).length} of {generatedBanners.length} banners ready
                </p>
              </div>

              {/* Filter by Platform */}
              <div className="mb-6">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-600">Filter by platform:</span>
                  <div className="flex gap-2">
                    {['All', 'Google Ads', 'Facebook', 'Instagram', 'LinkedIn', 'YouTube', 'Mobile'].map(platform => (
                      <button
                        key={platform}
                        className="px-3 py-1 text-xs border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
                      >
                        {platform}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Banner Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {generatedBanners.map((banner) => (
                  <div key={banner.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                    {/* Banner Preview */}
                    <div className="p-4 bg-gray-50 flex items-center justify-center min-h-32">
                      {banner.isReady ? (
                        <img 
                          src={banner.preview} 
                          alt={banner.name}
                          className="max-w-full max-h-full object-contain border border-gray-200 rounded"
                          style={{
                            maxWidth: banner.width > 400 ? '300px' : 'auto',
                            maxHeight: banner.height > 300 ? '200px' : 'auto'
                          }}
                        />
                      ) : (
                        <div className="flex items-center gap-2 text-gray-500">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm">Generating...</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Banner Info */}
                    <div className="p-4">
                      <h3 className="font-medium text-gray-800 mb-1">{banner.name}</h3>
                      <p className="text-xs text-gray-500 mb-2">{banner.platform}</p>
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-3">
                        <span>{banner.width} x {banner.height}px</span>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          banner.category === 'desktop' ? 'bg-blue-100 text-blue-700' :
                          banner.category === 'mobile' ? 'bg-green-100 text-green-700' :
                          banner.category === 'social' ? 'bg-purple-100 text-purple-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {banner.category}
                        </span>
                      </div>
                      
                      {/* Action Buttons */}
                      {banner.isReady && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => downloadBanner(banner)}
                            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            <Download className="w-3 h-3" />
                            Download
                          </button>
                          <button
                            onClick={() => copyBannerCode(banner)}
                            className="flex items-center justify-center px-3 py-2 border border-gray-300 text-gray-700 text-xs rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 